
import { MaintenanceAlertData } from '../types/maintenanceTypes';
import { Device } from '@/components/device-health/gis-map/types';

// Filter maintenance alerts based on asset type, priority, and facility
export const applyFilters = (
  alerts: MaintenanceAlertData[],
  assetTypeFilter: string | null,
  priorityFilter: string | null,
  facilityFilter: string | null
): MaintenanceAlertData[] => {
  return alerts.filter(alert => {
    // Filter by asset type
    if (assetTypeFilter && alert.assetType) {
      if (assetTypeFilter.toLowerCase() !== alert.assetType.toLowerCase() && 
          assetTypeFilter.toLowerCase() !== alert.deviceType.toLowerCase()) {
        return false;
      }
    }

    // Filter by priority
    if (priorityFilter) {
      if (
        (priorityFilter.toLowerCase() === 'critical' && alert.status !== 'Alert') ||
        (priorityFilter.toLowerCase() === 'medium' && alert.status !== 'Warning') ||
        (priorityFilter.toLowerCase() === 'low' && alert.status !== 'Good')
      ) {
        return false;
      }
    }

    // Filter by facility
    if (facilityFilter && alert.facility) {
      const facilityName = facilityFilter.replace(/plant/i, 'Plant ').replace(/datacenter/i, 'Data Center');
      if (!alert.facility.toLowerCase().includes(facilityName.toLowerCase())) {
        return false;
      }
    }

    return true;
  });
};

// Create a placeholder alert for devices that don't have specific alerts
export const createPlaceholderAlert = (device: Device): MaintenanceAlertData => {
  // Handle location formatting with proper type checking
  let locationStr: string = 'Unknown Location';
  let facilityInfo: string = 'Unknown';

  // Check if location exists
  if (device.location) {
    // Handle location as an object (the correct type according to Device interface)
    if (typeof device.location === 'object') {
      locationStr = `${device.location.lat || 0}, ${device.location.lng || 0}`;
      
      // If location.address exists, try to extract facility info from it
      if ('address' in device.location && typeof device.location.address === 'string') {
        const addressParts = device.location.address.split(',');
        facilityInfo = addressParts.length > 0 ? addressParts[0] : 'Unknown';
      }
    } 
    // This is a fallback in case location is somehow a string (should not happen based on types)
    else if (typeof device.location === 'string') {
      locationStr = device.location;
      
      // Safety check before using string methods
      if (locationStr.includes(' – ')) {
        facilityInfo = locationStr.split(' – ')[0];
      }
    }
  }
    
  const baseAlert: MaintenanceAlertData = {
    title: `${device.name} Status`,
    status: device.status === 'Healthy' ? 'Good' : device.status === 'Warning' ? 'Warning' : 'Alert',
    icon: null, // Will be set by the component that renders the alert
    bgClass: device.status === 'Healthy' ? 'bg-green-500' : device.status === 'Warning' ? 'bg-amber-500' : 'bg-red-500',
    deviceId: device.id,
    deviceType: device.type.toLowerCase(),
    reportDate: new Date().toISOString().split('T')[0],
    assetName: device.name,
    assetType: device.type,
    location: locationStr,
    facility: facilityInfo
  };

  // Add status-specific properties
  if (device.status === 'Healthy') {
    baseAlert.nextInspection = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  } else {
    baseAlert.issue = `${device.type} requires attention`;
    baseAlert.aiPrediction = device.status === 'Warning' 
      ? 'Maintenance recommended within 30 days' 
      : 'Service required within 7 days';
    baseAlert.confidence = device.status === 'Warning' ? 75 : 92;
  }

  return baseAlert;
};
